// ============================================================================
// Arithmétique des ENTIERS, exacte (refonte, lot 5.10, moteur v35).
// ============================================================================
// Pur, sans dépendance : lecture d'une ligne d'arithmétique (« 1071 = 2 × 462 + 147 »,
// « PGCD(1071 ; 462) = PGCD(462 ; 147) », « 1 = 5 − 2 × (17 − 3 × 5) », « u = −2 ; v = 7 »,
// « 7 × (−11) ≡ 1 [26] »), calcul exact sur BigInt, algorithme d'Euclide, et lecture
// d'une expression comme COMBINAISON des restes de l'algorithme (la remontée de Bézout).
//
// Une combinaison est une forme linéaire sur les nombres de la suite d'Euclide
// (r₀ = a, r₁ = b, r₂, …, r_m = PGCD) : « 7 × 5 − 2 × 17 » se lit 7·[5] − 2·[17]. Un
// produit de deux nombres de la suite (« 2 × 5 ») a deux lectures (2·[5] ou 5·[2]) : on
// les garde toutes, le juge choisit celle qui découle de la ligne précédente.
// ============================================================================

/** une erreur de lecture, pour l'élève */
export class ArithSyntaxError extends Error {}
/** une ligne inachevée (en cours de frappe) */
export class ArithIncomplete extends Error {}

export type AExpr =
  | { k: 'num'; v: bigint }
  | { k: 'gcd'; a: bigint; b: bigint }
  /** moteur v51 : « PPCM(12 ; 18) » */
  | { k: 'lcm'; a: bigint; b: bigint }
  /** moteur v51 : une puissance entière (« 2³ », « (2 × 3)⁵ ») */
  | { k: 'pow'; b: AExpr; e: bigint }
  | { k: 'sym'; n: string }
  | { k: 'add'; t: { s: 1 | -1; e: AExpr }[] }
  | { k: 'mul'; f: AExpr[] }
  | { k: 'paren'; e: AExpr }
  | { k: 'neg'; e: AExpr };

export type ARel = '=' | '≡';
/** une chaîne « a = b = c » (ou « a ≡ b [n] ») */
export interface AChain {
  members: AExpr[];
  rels: ARel[];
  mod: bigint | null;
}
/** une ligne : une ou plusieurs chaînes séparées par « ; » (« u = −2 ; v = 7 ») */
export interface ALine {
  parts: AChain[];
}

const MINUS = '−';
/** « PGCD(1071 ; 462) », écrit comme un seul bout */
const GCD_RE = /^(?:PGCD|pgcd|gcd)\s*\(\s*([−-]?)\s*(\d+)\s*[;,]\s*([−-]?)\s*(\d+)\s*\)/;
/** « PPCM(12 ; 18) » (moteur v51), écrit comme un seul bout */
const LCM_RE = /^(?:PPCM|ppcm|lcm)\s*\(\s*([−-]?)\s*(\d+)\s*[;,]\s*([−-]?)\s*(\d+)\s*\)/;
/** un exposant (« ³ », « ¹⁰ ») */
const SUP_RE = /^[⁰¹²³⁴⁵⁶⁷⁸⁹]+/;
const SUPS = '⁰¹²³⁴⁵⁶⁷⁸⁹';
/** le modulo d'une congruence « [26] » */
const MOD_RE = /^\[\s*(\d+)\s*\]/;
const NUM_RE = /^\d+/;
const SYM_RE = /^[a-zA-Z](?:[₀-₉]+)?/;
const OPS: Record<string, string> = {
  '+': '+',
  '-': MINUS,
  [MINUS]: MINUS,
  '×': '×',
  '*': '×',
  '·': '×',
  '(': '(',
  ')': ')',
  '=': '=',
  '≡': '≡',
  ';': ';',
  '÷': '÷',
  '/': '÷',
};

/** un bout « PGCD(a ; b) » */
export const isGcdAtom = (a: string | undefined): boolean => !!a && /^PGCD\(/.test(a);
/** un bout « [n] » */
export const isModAtom = (a: string | undefined): boolean => !!a && /^\[\d+\]$/.test(a);
/** un bout « PPCM(a ; b) » (moteur v51) */
export const isLcmAtom = (a: string | undefined): boolean => !!a && /^PPCM\(/.test(a);
/** un bout exposant « ³ », « ¹⁰ » (moteur v51) */
export const isSupAtom = (a: string | undefined): boolean => !!a && /^[⁰¹²³⁴⁵⁶⁷⁸⁹]+$/.test(a);
export const lcmAtom = (a: bigint, b: bigint): string => `PPCM(${numText(a)} ; ${numText(b)})`;
/** l'exposant écrit en chiffres supérieurs : 10 → « ¹⁰ » */
export const supText = (n: bigint): string =>
  String(n)
    .split('')
    .map((d) => SUPS[Number(d)])
    .join('');
const supValue = (s: string): bigint => BigInt([...s].map((c) => SUPS.indexOf(c)).join(''));
export const gcdAtom = (a: bigint, b: bigint): string => `PGCD(${numText(a)} ; ${numText(b)})`;
export const modAtom = (n: bigint): string => `[${n}]`;
export const numText = (v: bigint): string => (v < 0n ? MINUS + String(-v) : String(v));

/** Les bouts d'une ligne : nombres, « PGCD(a ; b) », lettres, opérations, « [n] ». */
export function arithLineAtoms(text: string): string[] {
  const out: string[] = [];
  let s = text.replace(/\u202f|\u00a0/g, ' ');
  while (s.length) {
    const ws = /^\s+/.exec(s);
    if (ws) {
      s = s.slice(ws[0].length);
      continue;
    }
    const g = GCD_RE.exec(s);
    if (g) {
      const a = BigInt(g[2]!) * (g[1] ? -1n : 1n);
      const b = BigInt(g[4]!) * (g[3] ? -1n : 1n);
      out.push(gcdAtom(a, b));
      s = s.slice(g[0].length);
      continue;
    }
    const l = LCM_RE.exec(s);
    if (l) {
      const a = BigInt(l[2]!) * (l[1] ? -1n : 1n);
      const b = BigInt(l[4]!) * (l[3] ? -1n : 1n);
      out.push(lcmAtom(a, b));
      s = s.slice(l[0].length);
      continue;
    }
    const sup = SUP_RE.exec(s);
    if (sup) {
      out.push(sup[0]);
      s = s.slice(sup[0].length);
      continue;
    }
    const md = MOD_RE.exec(s);
    if (md) {
      out.push(`[${BigInt(md[1]!)}]`);
      s = s.slice(md[0].length);
      continue;
    }
    const n = NUM_RE.exec(s);
    if (n) {
      out.push(String(BigInt(n[0])));
      s = s.slice(n[0].length);
      continue;
    }
    const sym = SYM_RE.exec(s);
    if (sym) {
      out.push(sym[0]);
      s = s.slice(sym[0].length);
      continue;
    }
    const op = OPS[s[0]!];
    if (op) {
      out.push(op);
      s = s.slice(1);
      continue;
    }
    throw new ArithSyntaxError(`« ${s[0]} » ne se lit pas ici.`);
  }
  return out;
}

/* ------------------------------ lecture ------------------------------ */

const isNumAtom = (a: string | undefined): boolean => !!a && /^\d+$/.test(a);
const isSymAtom = (a: string | undefined): boolean => !!a && /^[a-zA-Z][₀-₉]*$/.test(a);

class Reader {
  i = 0;
  constructor(readonly a: string[]) {}
  peek(): string | undefined {
    return this.a[this.i];
  }
  next(): string | undefined {
    return this.a[this.i++];
  }
  end(): boolean {
    return this.i >= this.a.length;
  }
}

function parseExpr(r: Reader): AExpr {
  const terms: { s: 1 | -1; e: AExpr }[] = [];
  let s: 1 | -1 = 1;
  if (r.peek() === '+') r.next();
  terms.push({ s, e: parseTerm(r) });
  for (;;) {
    const p = r.peek();
    if (p !== '+' && p !== MINUS) break;
    r.next();
    s = p === '+' ? 1 : -1;
    terms.push({ s, e: parseTerm(r) });
  }
  return terms.length === 1 && terms[0]!.s === 1 ? terms[0]!.e : { k: 'add', t: terms };
}

/** un facteur peut suivre un autre sans « × » : « 17u », « 2(x + 1) », « 3PGCD(…) » non */
const startsPrimary = (a: string | undefined): boolean =>
  isNumAtom(a) || isSymAtom(a) || isGcdAtom(a) || isLcmAtom(a) || a === '(';

function parseTerm(r: Reader): AExpr {
  const f: AExpr[] = [parseUnary(r)];
  for (;;) {
    const p = r.peek();
    if (p === '×') {
      r.next();
      f.push(parseUnary(r));
      continue;
    }
    if (p === '÷')
      throw new ArithSyntaxError('Écris la division euclidienne sous la forme « a = q × b + r ».');
    if (startsPrimary(p)) {
      const last = f[f.length - 1]!;
      if (isNumAtom(p) && (last.k === 'num' || last.k === 'neg' || last.k === 'pow'))
        throw new ArithSyntaxError('Il manque une opération entre deux nombres.');
      f.push(parseUnary(r));
      continue;
    }
    break;
  }
  return f.length === 1 ? f[0]! : { k: 'mul', f };
}

function parseUnary(r: Reader): AExpr {
  if (r.peek() === MINUS) {
    r.next();
    return { k: 'neg', e: parseUnary(r) };
  }
  return parsePower(r);
}

/** un facteur et ses exposants (« 2³ », « (2 × 3)⁵ ») : l'exposant lie plus fort que « − » */
function parsePower(r: Reader): AExpr {
  let p = parsePrimary(r);
  while (isSupAtom(r.peek())) {
    const e = supValue(r.next()!);
    if (e > 64n) throw new ArithSyntaxError('Un exposant plus petit, s’il te plaît.');
    p = { k: 'pow', b: p, e };
  }
  return p;
}

function parsePrimary(r: Reader): AExpr {
  const a = r.next();
  if (a === undefined) throw new ArithIncomplete('ligne inachevée');
  if (isNumAtom(a)) return { k: 'num', v: BigInt(a) };
  if (isGcdAtom(a)) {
    const m = /^PGCD\((−?\d+) ; (−?\d+)\)$/.exec(a)!;
    return { k: 'gcd', a: bigOf(m[1]!), b: bigOf(m[2]!) };
  }
  if (isLcmAtom(a)) {
    const m = /^PPCM\((−?\d+) ; (−?\d+)\)$/.exec(a)!;
    return { k: 'lcm', a: bigOf(m[1]!), b: bigOf(m[2]!) };
  }
  if (isSupAtom(a)) throw new ArithSyntaxError('Un exposant se place après un nombre.');
  if (isSymAtom(a)) return { k: 'sym', n: a };
  if (a === '(') {
    if (r.end()) throw new ArithIncomplete('parenthèse ouverte');
    const e = parseExpr(r);
    if (r.end()) throw new ArithIncomplete('parenthèse ouverte');
    if (r.next() !== ')') throw new ArithSyntaxError('Une parenthèse n’est pas fermée.');
    return { k: 'paren', e };
  }
  if (a === ')') throw new ArithSyntaxError('Une parenthèse fermée de trop.');
  throw new ArithSyntaxError(`« ${a} » ne se place pas là.`);
}

const bigOf = (t: string): bigint => (t.startsWith(MINUS) ? -BigInt(t.slice(1)) : BigInt(t));

function parseChain(atoms: string[]): AChain {
  if (!atoms.length) throw new ArithIncomplete('membre vide');
  let mod: bigint | null = null;
  let body = atoms;
  if (isModAtom(atoms[atoms.length - 1])) {
    mod = BigInt(atoms[atoms.length - 1]!.slice(1, -1));
    body = atoms.slice(0, -1);
    if (mod < 2n) throw new ArithSyntaxError('Le modulo est un entier supérieur ou égal à 2.');
  }
  if (body.some((a) => isModAtom(a)))
    throw new ArithSyntaxError('Le modulo « [n] » s’écrit à la fin de la ligne.');
  const r = new Reader(body);
  const members: AExpr[] = [parseExpr(r)];
  const rels: ARel[] = [];
  while (!r.end()) {
    const p = r.next()!;
    if (p !== '=' && p !== '≡') throw new ArithSyntaxError(`« ${p} » ne se place pas là.`);
    if (r.end()) throw new ArithIncomplete('membre vide');
    rels.push(p);
    members.push(parseExpr(r));
  }
  if (mod !== null && !rels.includes('≡'))
    throw new ArithSyntaxError('Un modulo « [n] » va avec « ≡ ».');
  if (mod === null && rels.includes('≡')) throw new ArithIncomplete('modulo à écrire');
  return { members, rels, mod };
}

/** Lit une ligne (bouts) ; `ArithIncomplete` si elle est inachevée. */
export function parseArithLine(atoms: string[]): ALine {
  const parts: string[][] = [[]];
  atoms.forEach((a) => (a === ';' ? parts.push([]) : parts[parts.length - 1]!.push(a)));
  return { parts: parts.map(parseChain) };
}

/* ------------------------------ écriture ------------------------------ */

export function exprText(e: AExpr): string {
  switch (e.k) {
    case 'num':
      return numText(e.v);
    case 'gcd':
      return gcdAtom(e.a, e.b);
    case 'lcm':
      return lcmAtom(e.a, e.b);
    case 'pow':
      return exprText(e.b) + supText(e.e);
    case 'sym':
      return e.n;
    case 'paren':
      return `(${exprText(e.e)})`;
    case 'neg':
      return MINUS + exprText(e.e);
    case 'mul':
      return e.f
        .map((x, i) => {
          const t = exprText(x);
          const prev = e.f[i - 1];
          if (!i) return t;
          // « 17u » : un nombre suivi d'une lettre
          return prev && prev.k === 'num' && x.k === 'sym' ? t : ' × ' + t;
        })
        .join('');
    case 'add':
      return e.t
        .map((x, i) =>
          i === 0
            ? (x.s < 0 ? MINUS : '') + exprText(x.e)
            : (x.s < 0 ? ` ${MINUS} ` : ' + ') + exprText(x.e),
        )
        .join('');
  }
}

export function chainText(c: AChain): string {
  let t = exprText(c.members[0]!);
  c.rels.forEach((r, i) => (t += ` ${r} ${exprText(c.members[i + 1]!)}`));
  return c.mod !== null ? `${t} [${c.mod}]` : t;
}

/** écriture canonique d'une ligne (comparaisons : énoncé, pièges, lignes déjà écrites) */
export function arithText(atoms: string[]): string {
  try {
    return parseArithLine(atoms).parts.map(chainText).join(' ; ');
  } catch {
    return atoms.join(' ');
  }
}

/* ------------------------------ calcul ------------------------------ */

export const absB = (v: bigint): bigint => (v < 0n ? -v : v);
export function gcdB(a: bigint, b: bigint): bigint {
  let x = absB(a);
  let y = absB(b);
  while (y) [x, y] = [y, x % y];
  return x;
}
/** le PPCM de deux entiers (positif) */
export function lcmB(a: bigint, b: bigint): bigint {
  if (a === 0n || b === 0n) return 0n;
  return absB(a * b) / gcdB(a, b);
}
/** reste entre 0 et n − 1 */
export const modB = (v: bigint, n: bigint): bigint => ((v % n) + n) % n;

/** valeur exacte ; null si une lettre n'a pas de valeur */
export function evalA(e: AExpr, env?: Map<string, bigint>): bigint | null {
  switch (e.k) {
    case 'num':
      return e.v;
    case 'gcd':
      return gcdB(e.a, e.b);
    case 'lcm':
      return lcmB(e.a, e.b);
    case 'pow': {
      const v = evalA(e.b, env);
      return v === null ? null : v ** e.e;
    }
    case 'sym':
      return env?.get(e.n) ?? null;
    case 'paren':
      return evalA(e.e, env);
    case 'neg': {
      const v = evalA(e.e, env);
      return v === null ? null : -v;
    }
    case 'mul': {
      let p = 1n;
      for (const f of e.f) {
        const v = evalA(f, env);
        if (v === null) return null;
        p *= v;
      }
      return p;
    }
    case 'add': {
      let s = 0n;
      for (const t of e.t) {
        const v = evalA(t.e, env);
        if (v === null) return null;
        s += BigInt(t.s) * v;
      }
      return s;
    }
  }
}

/** un nombre écrit seul (« 147 », « −11 », « (−2) ») */
export function bareNumber(e: AExpr): bigint | null {
  if (e.k === 'num') return e.v;
  if (e.k === 'paren') return bareNumber(e.e);
  if (e.k === 'neg') {
    const v = bareNumber(e.e);
    return v === null ? null : -v;
  }
  return null;
}

/** les lettres d'une expression */
export function symsOf(e: AExpr, out = new Set<string>()): Set<string> {
  if (e.k === 'sym') out.add(e.n);
  else if (e.k === 'paren' || e.k === 'neg') symsOf(e.e, out);
  else if (e.k === 'pow') symsOf(e.b, out);
  else if (e.k === 'mul') e.f.forEach((x) => symsOf(x, out));
  else if (e.k === 'add') e.t.forEach((x) => symsOf(x.e, out));
  return out;
}
export function hasGcd(e: AExpr): boolean {
  if (e.k === 'gcd') return true;
  if (e.k === 'paren' || e.k === 'neg') return hasGcd(e.e);
  if (e.k === 'pow') return hasGcd(e.b);
  if (e.k === 'mul') return e.f.some(hasGcd);
  if (e.k === 'add') return e.t.some((x) => hasGcd(x.e));
  return false;
}
/** un PPCM écrit (moteur v51) */
export function hasLcm(e: AExpr): boolean {
  if (e.k === 'lcm') return true;
  if (e.k === 'paren' || e.k === 'neg') return hasLcm(e.e);
  if (e.k === 'pow') return hasLcm(e.b);
  if (e.k === 'mul') return e.f.some(hasLcm);
  if (e.k === 'add') return e.t.some((x) => hasLcm(x.e));
  return false;
}
/** une parenthèse qui contient une somme (« (17 − 3 × 5) ») : la ligne n'est pas regroupée */
export function hasParenSum(e: AExpr): boolean {
  if (e.k === 'paren') return e.e.k === 'add' || hasParenSum(e.e);
  if (e.k === 'neg') return hasParenSum(e.e);
  if (e.k === 'pow') return hasParenSum(e.b);
  if (e.k === 'mul') return e.f.some(hasParenSum);
  if (e.k === 'add') return e.t.some((x) => hasParenSum(x.e));
  return false;
}
/** les termes d'une somme écrite (« 7 × 5 − 2 × 17 » : 2 termes) */
export const topTerms = (e: AExpr): number => (e.k === 'add' ? e.t.length : 1);

/* ------------------------------ Euclide ------------------------------ */

export interface Euclid {
  /** r[0] = le plus grand, r[1] = le plus petit, …, r[m] = PGCD, r[m + 1] = 0 */
  r: bigint[];
  /** q[k] : quotient de la division k (r[k − 1] = q[k] × r[k] + r[k + 1]), k = 1…m */
  q: bigint[];
  /** nombre de divisions (la dernière a un reste nul) */
  m: number;
}

/** L'algorithme d'Euclide sur deux entiers strictement positifs. */
export function euclid(a: bigint, b: bigint): Euclid {
  let x = absB(a) >= absB(b) ? absB(a) : absB(b);
  let y = absB(a) >= absB(b) ? absB(b) : absB(a);
  if (y === 0n) throw new ArithSyntaxError('Le PGCD avec 0 ne demande pas l’algorithme.');
  const r: bigint[] = [x, y];
  const q: bigint[] = [0n];
  while (y) {
    q.push(x / y);
    const z = x % y;
    r.push(z);
    [x, y] = [y, z];
    if (r.length > 60) throw new ArithSyntaxError('Algorithme trop long.');
  }
  return { r, q, m: r.length - 2 };
}

/* ------------------------------ combinaisons ------------------------------ */

/** une forme linéaire sur les indices de la suite d'Euclide : indice → coefficient */
export type LinForm = Map<number, bigint>;
export interface Reading {
  lin: LinForm;
  /** partie constante (une vraie combinaison n'en a pas) */
  c: bigint;
}

export function linKey(l: LinForm): string {
  return [...l.entries()]
    .filter(([, c]) => c !== 0n)
    .sort((a, b) => a[0] - b[0])
    .map(([i, c]) => `${i}:${c}`)
    .join(',');
}
export const readingKey = (r: Reading): string => `${linKey(r.lin)}|${r.c}`;

function linScale(l: LinForm, k: bigint): LinForm {
  const out: LinForm = new Map();
  l.forEach((c, i) => c * k !== 0n && out.set(i, c * k));
  return out;
}
function linAdd(a: LinForm, b: LinForm): LinForm {
  const out: LinForm = new Map(a);
  b.forEach((c, i) => {
    const v = (out.get(i) ?? 0n) + c;
    if (v === 0n) out.delete(i);
    else out.set(i, v);
  });
  return out;
}

const dedupe = (rs: Reading[], cap: number): Reading[] => {
  const seen = new Set<string>();
  const out: Reading[] = [];
  for (const r of rs) {
    const k = readingKey(r);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(r);
    if (out.length >= cap) break;
  }
  return out;
};

/**
 * Les lectures d'une expression comme combinaison des nombres de la suite (`idx` :
 * valeur → indice). Un nombre est une constante, ou (s'il est dans la suite) un nombre de
 * la suite ; un produit garde au plus un facteur non constant.
 */
export function readings(e: AExpr, idx: Map<bigint, number>, cap = 256): Reading[] {
  switch (e.k) {
    case 'num': {
      const out: Reading[] = [{ lin: new Map(), c: e.v }];
      const i = idx.get(e.v);
      if (i !== undefined) out.push({ lin: new Map([[i, 1n]]), c: 0n });
      return out;
    }
    case 'gcd':
      return [{ lin: new Map(), c: gcdB(e.a, e.b) }];
    case 'lcm':
      return [{ lin: new Map(), c: lcmB(e.a, e.b) }];
    case 'pow': {
      const v = evalA(e);
      return v === null ? [] : [{ lin: new Map(), c: v }];
    }
    case 'sym':
      return [];
    case 'paren':
      return readings(e.e, idx, cap);
    case 'neg':
      return readings(e.e, idx, cap).map((r) => ({ lin: linScale(r.lin, -1n), c: -r.c }));
    case 'add': {
      let acc: Reading[] = [{ lin: new Map(), c: 0n }];
      for (const t of e.t) {
        const rs = readings(t.e, idx, cap);
        const next: Reading[] = [];
        for (const a of acc)
          for (const b of rs)
            next.push({
              lin: linAdd(a.lin, linScale(b.lin, BigInt(t.s))),
              c: a.c + BigInt(t.s) * b.c,
            });
        acc = dedupe(next, cap);
      }
      return acc;
    }
    case 'mul': {
      let acc: Reading[] = [{ lin: new Map(), c: 1n }];
      for (const f of e.f) {
        const rs = readings(f, idx, cap);
        const next: Reading[] = [];
        for (const a of acc)
          for (const b of rs) {
            // au plus un facteur non constant
            if (a.lin.size && b.lin.size) continue;
            if (a.lin.size) next.push({ lin: linScale(a.lin, b.c), c: 0n });
            else if (b.lin.size) next.push({ lin: linScale(b.lin, a.c), c: 0n });
            else next.push({ lin: new Map(), c: a.c * b.c });
          }
        acc = dedupe(next, cap);
      }
      return acc;
    }
  }
}

/** les lectures qui sont de vraies combinaisons (sans constante) */
export const combinations = (e: AExpr, idx: Map<bigint, number>): LinForm[] =>
  readings(e, idx)
    .filter((r) => r.c === 0n && r.lin.size > 0)
    .map((r) => r.lin);

/** remplace r[j] dans `f` par `by` (une forme de r[j − 2], r[j − 1]) */
export function substituteLin(f: LinForm, j: number, by: LinForm): LinForm {
  const k = f.get(j);
  if (k === undefined) return f;
  const rest: LinForm = new Map(f);
  rest.delete(j);
  return linAdd(rest, linScale(by, k));
}

/** valeur d'une forme */
export function linValue(f: LinForm, r: bigint[]): bigint {
  let s = 0n;
  f.forEach((c, i) => (s += c * r[i]!));
  return s;
}

/* ------------------------------ facteurs premiers (moteur v51) ------------------------------ */

/** n est-il premier ? (entiers de la feuille : petits) */
export function isPrimeB(n: bigint): boolean {
  if (n < 2n) return false;
  for (let d = 2n; d * d <= n; d++) if (n % d === 0n) return false;
  return true;
}

/** la décomposition en facteurs premiers : premier → exposant (n ≥ 2) */
export function primeFactors(n: bigint): Map<bigint, number> {
  const out = new Map<bigint, number>();
  let x = absB(n);
  for (let d = 2n; d * d <= x; d++)
    while (x % d === 0n) {
      out.set(d, (out.get(d) ?? 0) + 1);
      x /= d;
    }
  if (x > 1n) out.set(x, (out.get(x) ?? 0) + 1);
  return out;
}

/**
 * Les facteurs d'un produit écrit (« 2² × 3 × 7 », « 2 × 2 × 21 », « 5³ ») : base →
 * exposant, les bases répétées réunies ; null si l'écriture n'est pas un produit de
 * puissances d'entiers positifs.
 */
export function writtenFactors(e: AExpr): { b: bigint; e: number }[] | null {
  const s = e.k === 'paren' ? e.e : e;
  const fs = s.k === 'mul' ? s.f : [s];
  const out: { b: bigint; e: number }[] = [];
  for (const f of fs) {
    const g = f.k === 'paren' ? f.e : f;
    if (g.k === 'num' && g.v > 0n) out.push({ b: g.v, e: 1 });
    else if (g.k === 'pow') {
      const base = g.b.k === 'paren' ? g.b.e : g.b;
      if (base.k !== 'num' || base.v <= 0n) return null;
      out.push({ b: base.v, e: Number(g.e) });
    } else return null;
  }
  return out;
}

/** l'écriture est-elle un produit de nombres PREMIERS (« 2² × 3 × 7 », « 2 × 2 × 3 ») ? */
export function primeProductOf(e: AExpr): Map<bigint, number> | null {
  const fs = writtenFactors(e);
  if (!fs || !fs.length || fs.some((f) => !isPrimeB(f.b) || f.e < 1)) return null;
  const out = new Map<bigint, number>();
  fs.forEach((f) => out.set(f.b, (out.get(f.b) ?? 0) + f.e));
  return out;
}

export const sameFactors = (a: Map<bigint, number>, b: Map<bigint, number>): boolean =>
  a.size === b.size && [...a].every(([p, k]) => b.get(p) === k);

/** les facteurs communs au plus petit exposant (PGCD) ou tous au plus grand (PPCM) */
export function combineFactors(
  a: Map<bigint, number>,
  b: Map<bigint, number>,
  kind: 'gcd' | 'lcm',
): Map<bigint, number> {
  const out = new Map<bigint, number>();
  const primes = new Set([...a.keys(), ...b.keys()]);
  for (const p of primes) {
    const x = a.get(p) ?? 0;
    const y = b.get(p) ?? 0;
    const k = kind === 'gcd' ? Math.min(x, y) : Math.max(x, y);
    if (k > 0) out.set(p, k);
  }
  return out;
}
